import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Check, Minus, Plus, ShoppingCart, Trash2 } from 'lucide-react';
import { useOffers } from '@/hooks/useOffers';
import { useOfferServices, getRealPriceDisplay } from '@/hooks/offers/useOfferServices';
import { useIsMobile } from '@/hooks/use-mobile';
import { Skeleton } from '@/components/ui/skeleton';

interface SelectedOffer {
  id: string;
  title: string;
  price: number;
  quantity: number;
  quantifiable: boolean;
  priceUnit?: string;
}

// Composant pour afficher le prix d'une offre
const OfferPriceDisplay = ({ offerId }: { offerId: string }) => {
  const { data: offerServices } = useOfferServices(offerId);
  
  if (!offerServices || offerServices.length === 0) {
    return <Badge variant="secondary">Prix sur demande</Badge>;
  }

  // Calculer le prix minimum des services de l'offre
  const minPrice = Math.min(...offerServices.map(service => {
    const { price } = getRealPriceDisplay(service);
    return price;
  }));

  const firstService = offerServices[0];
  const { unitText } = getRealPriceDisplay(firstService);

  return (
    <Badge variant="outline" className="font-medium">Sur-mesure</Badge>
  );
};

interface ParticulierOffersStepProps {
  typeEvenement?: string;
  selectedOffers?: SelectedOffer[];
  onComplete: (data: { selectedOffers: SelectedOffer[] }) => void;
}

export const ParticulierOffersStep = ({ typeEvenement, selectedOffers = [], onComplete }: ParticulierOffersStepProps) => {
  const [currentSelection, setCurrentSelection] = useState<SelectedOffer[]>(selectedOffers);
  const { data: offers = [], isLoading } = useOffers();
  const isMobile = useIsMobile();

  // Pour l'instant, afficher toutes les offres (en attendant l'implémentation des gammes)
  const filteredOffers = offers;

  const addOffer = (offer: any) => {
    const newOffer: SelectedOffer = {
      id: offer.id,
      title: offer.title,
      price: 0, // Le prix sera calculé en fonction des services sélectionnés
      quantity: 1,
      quantifiable: offer.quantifiable || false,
    };
    setCurrentSelection(prev => [...prev, newOffer]);
  };

  const updateQuantity = (offerId: string, quantity: number) => {
    if (quantity <= 0) {
      setCurrentSelection(prev => prev.filter(o => o.id !== offerId));
    } else {
      setCurrentSelection(prev =>
        prev.map(o => o.id === offerId ? { ...o, quantity } : o)
      );
    }
  };

  const removeOffer = (offerId: string) => {
    setCurrentSelection(prev => prev.filter(o => o.id !== offerId));
  };

  const isOfferSelected = (offerId: string) => {
    return currentSelection.some(o => o.id === offerId);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="rounded-xl border p-3">
            <div className="flex items-center justify-between">
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-64" />
                <Skeleton className="h-5 w-28" />
              </div>
              <Skeleton className="h-9 w-20" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Offres disponibles */}
      <div className="space-y-4">
        <h3 className="font-medium">Offres disponibles — {typeEvenement}</h3>
        <p className="text-sm text-muted-foreground">
          Vous pouvez en sélectionner une ou plusieurs, puis ajuster les quantités si nécessaire.
          Besoin d'aide pour choisir ? Utilisez les actions rapides ci-dessus.
        </p>
        
        {filteredOffers.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-sm text-muted-foreground">Aucune offre disponible pour le moment.</p>
          </div>
        ) : (
          <div className={`grid gap-3 max-h-60 overflow-y-auto ${isMobile ? 'grid-cols-1' : 'grid-cols-1'}`}>
          {filteredOffers.map((offer) => (
            <Card key={offer.id} className={isMobile ? "p-2" : "p-3"}>
              <div className={`flex ${isMobile ? 'flex-col' : 'items-center justify-between'}`}>
                <div className="flex-1">
                  <h4 className={`font-medium ${isMobile ? 'text-center text-sm mb-2' : 'text-sm'}`}>
                    {offer.title}
                  </h4>
                  <p className={`text-xs text-muted-foreground ${isMobile ? 'text-center mb-2' : ''}`}>
                    {offer.description}
                  </p>
                  <div className={`flex ${isMobile ? 'justify-center' : 'items-center'} gap-2 mt-1`}>
                    <OfferPriceDisplay offerId={offer.id} />
                    {offer.quantifiable && <Badge variant="outline">Quantifiable</Badge>}
                  </div>
                </div>
                <Button
                  size={isMobile ? "default" : "sm"}
                  onClick={() => addOffer(offer)}
                  disabled={isOfferSelected(offer.id)}
                  variant={isOfferSelected(offer.id) ? "secondary" : "default"}
                  className={isMobile ? "w-full mt-2" : ""}
                  aria-label={isOfferSelected(offer.id) ? "Ajouté" : "Ajouter"}
                >
                  {isOfferSelected(offer.id) ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <ShoppingCart className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </Card>
          ))}
          </div>
        )}
      </div>

      {/* Panier */}
      {currentSelection.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-medium">Votre sélection</h3>
          <div className="space-y-2">
            {currentSelection.map((offer) => (
              <Card key={offer.id} className={isMobile ? "p-2" : "p-3"}>
                <div className={`flex ${isMobile ? 'flex-col' : 'items-center justify-between'}`}>
                  <div className="flex-1">
                    <h4 className={`font-medium ${isMobile ? 'text-center text-sm mb-2' : 'text-sm'}`}>
                      {offer.title}
                    </h4>
                    <div className={`flex ${isMobile ? 'justify-center flex-wrap' : 'items-center'} gap-2 mt-1`}>
                      <OfferPriceDisplay offerId={offer.id} />
                      {offer.quantifiable && (
                        <div className={`flex items-center gap-1 ${isMobile ? 'justify-center' : ''}`}>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateQuantity(offer.id, offer.quantity - 1)}
                          >
                            <Minus className="w-3 h-3" />
                          </Button>
                          <Input
                            type="number"
                            value={offer.quantity}
                            onChange={(e) => updateQuantity(offer.id, parseInt(e.target.value) || 0)}
                            className="w-16 h-8 text-center"
                            min="1"
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateQuantity(offer.id, offer.quantity + 1)}
                          >
                            <Plus className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => removeOffer(offer.id)}
                    className={isMobile ? "w-full mt-2" : ""}
                  >
                    <Trash2 className="w-4 h-4" />
                    {isMobile && <span className="ml-2">Supprimer</span>}
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      <Button
        onClick={() => onComplete({ selectedOffers: currentSelection })}
        disabled={currentSelection.length === 0}
        className="w-full"
      >
        Continuer ({currentSelection.length} offre{currentSelection.length > 1 ? 's' : ''} sélectionnée{currentSelection.length > 1 ? 's' : ''})
      </Button>
    </div>
  );
};